import { Stack } from '@instabox/consumer-web-ui'
import { GetStaticProps, NextPage } from 'next'
import {
  GetFaqCategoriesQuery,
  GetFaqCategoriesQueryVariables,
  GetFaqsQuery,
  GetFaqsQueryVariables,
  GET_FAQ_CATEGORIES_QUERY,
  GET_FAQS_QUERY,
  PageProps,
  localeToMarketCode,
  apolloClient,
  fetchMarketById,
  fetchPage,
  fetchPageLinks,
  fetchBanners,
} from '@instabox/consumer-web-shared'

import ContactUs from '../../components/ContactUs'
import FAQCategory from '../../components/FAQCategory'
import Masonry from '../../components/Masonry'
import PageContent from '../../components/PageContent'
import WorkWithUs from '../../components/WorkWithUs'
import FAQSearchContainer from '../../containers/FAQSearchContainer'
import { CategoryWithFaqs } from '../../types'
import getCategoriesWithFaqs from '../../utils/mapCategoriesWithFaqs'

export interface HelpPageProps extends PageProps {
  categories: CategoryWithFaqs[]
  faqCategories: GetFaqCategoriesQuery['faqCategories']
  faqs: GetFaqsQuery['faqs']
}

const SLUG = 'help'

const HelpPage: NextPage<HelpPageProps> = ({
  categories = [],
  faqCategories = [],
  faqs = [],
  market,
  content,
}) => (
  <Stack gap="xl">
    <PageContent content={content} slug={SLUG} />
    <FAQSearchContainer categories={faqCategories} faqs={faqs} />
    <Masonry>
      {categories.map(cat => (
        <FAQCategory {...cat} key={cat.id} />
      ))}
    </Masonry>
    {market && (
      <div className="row">
        <div className="col-12 col-md-6">
          <ContactUs {...market} />
        </div>
        <div className="col-12 col-md-6">
          <WorkWithUs {...market} />
        </div>
      </div>
    )}
  </Stack>
)

export default HelpPage

export const getStaticProps: GetStaticProps<HelpPageProps> = async ({ locale }) => {
  const marketCode = localeToMarketCode(locale)
  const translationsFilter = {
    languages_code: {
      _eq: locale,
    },
  }
  const {
    data: { faqs },
  } = await apolloClient.query<GetFaqsQuery, GetFaqsQueryVariables>({
    query: GET_FAQS_QUERY,
    variables: {
      filter: {
        markets: {
          markets_code: { code: { _eq: marketCode } },
        },
      },
      translations_filter: translationsFilter,
    },
  })

  const {
    data: { faqCategories },
  } = await apolloClient.query<GetFaqCategoriesQuery, GetFaqCategoriesQueryVariables>({
    query: GET_FAQ_CATEGORIES_QUERY,
    variables: {
      filter: {
        type: {
          _neq: 'sub',
        },
      },
      translations_filter: translationsFilter,
    },
  })

  const categoriesWithFaqs = getCategoriesWithFaqs({ faqCategories, faqs })

  const {
    data: { marketById },
  } = await fetchMarketById(marketCode)

  const page = await fetchPage(locale, SLUG)
  const pageLinks = await fetchPageLinks(locale)
  const banners = await fetchBanners(locale, SLUG)

  return {
    props: {
      categories: categoriesWithFaqs,
      faqCategories,
      faqs,
      market: marketById,
      pageLinks,
      banners,
      ...page,
    },
    revalidate: 60,
  }
}
